import { useCallback, useEffect, useState } from "react";

function read(userId: string | null): string[] {
  if (!userId) return [];
  try {
    const value: unknown = JSON.parse(
      localStorage.getItem(`ato.library.favorites.v1:${userId}`) ?? "[]",
    );
    return Array.isArray(value)
      ? value.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

/** A per-account, per-device Library preference, independent from public Likes. */
export function useLibraryFavorites(userId: string | null) {
  const [snapshot, setSnapshot] = useState(() => ({
    userId,
    ids: read(userId),
  }));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setSnapshot({ userId, ids: read(userId) });
    setError(null);
    const refresh = (event: StorageEvent) => {
      if (event.key === `ato.library.favorites.v1:${userId}`)
        setSnapshot({ userId, ids: read(userId) });
    };
    window.addEventListener("storage", refresh);
    return () => window.removeEventListener("storage", refresh);
  }, [userId]);
  const toggle = useCallback(
    (id: string) => {
      if (!userId) return null;
      const previous = read(userId);
      const ids = previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id];
      try {
        localStorage.setItem(
          `ato.library.favorites.v1:${userId}`,
          JSON.stringify(ids),
        );
        setSnapshot({ userId, ids });
        setError(null);
        return previous.includes(id) ? "removed" : "added";
      } catch {
        setError("Couldn't save favorites on this device.");
        return null;
      }
    },
    [userId],
  );
  return { ids: snapshot.userId === userId ? snapshot.ids : [], toggle, error };
}
